import React, { useState, useEffect } from 'react';
import FormInput from '../forms/FormInput';
import SelectInput from '../forms/SelectInput';
import ProductImageUpload from './ProductImageUpload';
import Button from '../common/Button';
import Card from '../common/Card';
import { categoryService } from '../../services/category.service';
import { theme } from '../../config/theme';

const ProductForm = ({ 
  formData, 
  images,
  existingImages,
  errors, 
  touched,
  loading,
  isEdit = false,
  onChange, 
  onBlur,
  onImageAdd,
  onImageRemove,
  onExistingImageRemove,
  onTagAdd,
  onTagRemove,
  onSubmit,
  onCancel,
}) => {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getAll({ is_active: true });
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const categoryOptions = categories.map(cat => ({
    value: cat.id,
    label: cat.name,
  }));


  return (
    <form onSubmit={onSubmit}>
      <div style={{ display: 'grid', gap: theme.spacing.xl }}>
        {/* Basic Information */}
        <Card title="Basic Information" padding="lg">
          <SelectInput
            label="Category"
            name="category_id"
            value={formData.category_id}
            onChange={onChange}
            onBlur={onBlur}
            options={categoryOptions}
            error={errors.category_id}
            touched={touched.category_id}
            required
          />

          <FormInput
            label="Product Name"
            name="name"
            value={formData.name}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="Enter product name"
            error={errors.name}
            touched={touched.name}
            required
          />

          <FormInput
            label="Slug (Optional)"
            name="slug"
            value={formData.slug}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="auto-generated-from-name"
            error={errors.slug}
            touched={touched.slug}
          />

          <div style={{ marginBottom: theme.spacing.md }}>
            <label style={{
              display: 'block',
              marginBottom: theme.spacing.xs,
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
            }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Describe your product..."
              rows={5}
              style={{
                width: '100%',
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                fontSize: theme.typography.fontSize.sm,
                border: `1px solid ${errors.description && touched.description ? theme.colors.error : theme.colors.gray[300]}`,
                borderRadius: theme.borderRadius.md,
                fontFamily: theme.typography.fontFamily,
                resize: 'vertical',
              }}
            />
            {errors.description && touched.description && (
              <div style={{ marginTop: theme.spacing.xs, fontSize: theme.typography.fontSize.xs, color: theme.colors.error }}>
                {errors.description}
              </div>
            )}
          </div>

          {/* Product Condition */}
          <div style={{ marginBottom: theme.spacing.md }}>
            <label style={{
              display: 'block',
              marginBottom: theme.spacing.sm,
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
            }}>
              Product Condition <span style={{ color: theme.colors.error }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: theme.spacing.sm }}>
              {[
                { value: 'new',  label: '✨ New',  desc: 'Brand new, unused item' },
                { value: 'used', label: '🔄 Used', desc: 'Previously owned item'  },
              ].map(opt => {
                const isSelected = formData.condition === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange({ target: { name: 'condition', value: opt.value } })}
                    style={{
                      flex: 1,
                      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                      border: `2px solid ${isSelected ? theme.colors.primary.main : theme.colors.gray[300]}`,
                      borderRadius: theme.borderRadius.md,
                      backgroundColor: isSelected ? `${theme.colors.primary.main}12` : '#fff',
                      color: isSelected ? theme.colors.primary.main : theme.colors.text.secondary,
                      fontFamily: theme.typography.fontFamily,
                      fontSize: theme.typography.fontSize.sm,
                      fontWeight: isSelected ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.medium,
                      cursor: 'pointer',
                      transition: 'all 0.18s ease',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: theme.typography.fontSize.base }}>{opt.label}</div>
                    <div style={{ fontSize: theme.typography.fontSize.xs, marginTop: '2px', opacity: 0.75 }}>{opt.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </Card>

        {/* Images */}
        <Card title="Product Images" padding="lg">
          <ProductImageUpload
            images={images}
            existingImages={existingImages}
            onImageAdd={onImageAdd}
            onImageRemove={onImageRemove}
            onExistingImageRemove={onExistingImageRemove}
          />
        </Card>

        {/* Pricing */}
        <Card title="Pricing" padding="lg">
          <FormInput
            label="Price"
            name="price"
            type="number"
            step="0.01"
            value={formData.price}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="0.00"
            error={errors.price}
            touched={touched.price}
            required
          />

          <FormInput
            label="Cost per Item"
            name="cost_per_item"
            type="number"
            step="0.01"
            value={formData.cost_per_item}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="0.00"
            error={errors.cost_per_item}
            touched={touched.cost_per_item}
          />
        </Card>

        {/* Inventory */}
        <Card title="Inventory" padding="lg">
          <FormInput
            label="Barcode"
            name="barcode"
            value={formData.barcode}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="123456789"
            error={errors.barcode}
            touched={touched.barcode}
          />

          <div style={{ marginBottom: theme.spacing.md }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
              <input
                type="checkbox"
                name="track_inventory"
                checked={formData.track_inventory}
                onChange={onChange}
              />
              <span style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium }}>
                Track inventory
              </span>
            </label>
          </div>

          {formData.track_inventory && (
            <FormInput
              label="Quantity"
              name="quantity"
              type="number"
              value={formData.quantity}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="0"
              error={errors.quantity}
              touched={touched.quantity}
            />
          )}

          <FormInput
            label="Number of pieces"
            name="number_of_pieces"
            type="number"
            min="1"
            value={formData.number_of_pieces}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="1"
            error={errors.number_of_pieces}
            touched={touched.number_of_pieces}
            helperText="Number of pieces in one quantity unit"
          />

          <FormInput
            label="Weight (kg)"
            name="weight"
            type="number"
            step="0.01"
            value={formData.weight}
            onChange={onChange}
            onBlur={onBlur}
            placeholder="0.00"
            error={errors.weight}
            touched={touched.weight}
          />
        </Card>



        {/* Actions */}
        <div style={{ display: 'flex', gap: theme.spacing.md, justifyContent: 'flex-end' }}>
          <Button 
            variant="secondary" 
            size="md"
            onClick={onCancel} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="primary"
            size="md"
            loading={loading}
          >
            {isEdit ? 'Update Product' : 'Create Product'}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ProductForm;